import React, { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { ButtonToolbar } from 'reactstrap';
import { parse } from 'date-fns';
import { useHistory } from 'react-router-dom';
import {
  COMMUNICATION_TYPE,
  PENDENCY_DETAILS_TYPE,
} from '../../../shared/utils/constants';
import SmartButton from '../../../shared/components/SmartButton';
import AppointmentDetail from './AppointmentDetail';
import CommunicationDetail from './CommunicationDetail';
import EditAppointmentModal from '../../Appointment/components/EditAppointmentModal';
import FullScreenDialog from '../../../shared/components/FullScreenDialog';
import CommunicationFormHandler from '../../Communication/components/FormHandler';
import PeriodicAnalysisFormHandler from '../../PeriodicAnalysis/components/FormHandler';
import EventFormHandler from '../../Calendar/components/EventFormHandler';
import MeasuresFormHandler from '../../WeightMeasure/components/FormHandler';
import { anthropometricMeasurementsPropTypes } from '../../../shared/utils/SmartPropTypes';

const PendencyDetail = forwardRef((props, ref) => {
  const {
    t,
    type,
    selectedAppointmentItems,
    selectedCommunicationItems,
    updateAppointmentItem,
    onAppointmentRelations,
    onCloseEditCommunication,
    onCommunicationRelations,
    onDisapprove,
    onApprove,
  } = props;

  const history = useHistory();
  const selectedItem = selectedCommunicationItems[0];
  const [editFullScreenForm, setEditFullScreenForm] = useState({
    title: '',
    isOpen: false,
  });

  const showEditButton = () => {
    switch (type) {
      case PENDENCY_DETAILS_TYPE.APPOINTMENT:
        return selectedAppointmentItems.length === 1;
      case PENDENCY_DETAILS_TYPE.COMMUNICATION:
        return (
          selectedCommunicationItems.length === 1 &&
          selectedItem.type !== COMMUNICATION_TYPE.ANALYSIS
        );
      default:
        return false;
    }
  };

  const renderDetailContent = () => {
    switch (type) {
      case PENDENCY_DETAILS_TYPE.APPOINTMENT:
        return (
          <>
            {selectedAppointmentItems.map((appointment) => (
              <AppointmentDetail
                key={appointment.id}
                appointment={appointment}
                onAppointmentSelect={() => onAppointmentRelations(appointment)}
              />
            ))}
          </>
        );
      case PENDENCY_DETAILS_TYPE.COMMUNICATION:
        return (
          <>
            {selectedCommunicationItems.map((communication) => (
              <CommunicationDetail
                key={communication.id}
                communication={communication}
                onCommunicationSelect={() =>
                  onCommunicationRelations(communication)
                }
              />
            ))}
          </>
        );
      default:
        return <></>;
    }
  };

  const getFullScreenTitle = () => {
    return selectedItem
      ? t(`communication.type-${selectedItem.type}`)
      : t('mnt_appointment.appointment-edit');
  };

  const toggleEditFullScreenForm = () => {
    const updatedEditFullScreenForm = { ...editFullScreenForm };
    updatedEditFullScreenForm.title = getFullScreenTitle();
    updatedEditFullScreenForm.isOpen = !updatedEditFullScreenForm.isOpen;
    setEditFullScreenForm(updatedEditFullScreenForm);
  };

  const getFormattedAppointmentObject = () => {
    const item = selectedAppointmentItems[0];
    const dateNoteInMilli = parse(
      item.dateNote,
      'dd/MM/yyyy',
      new Date()
    ).getTime();
    return {
      ...item,
      class: item.class.name,
      dateNote: dateNoteInMilli,
    };
  };

  const getMatch = () => {
    return { params: { type: selectedItem.type, id: selectedItem.id } };
  };

  const getAnthropometricMatch = () => {
    return { params: { id: selectedItem.anthropometricMeasurements.id } };
  };

  const getHistory = () => {
    const updatedHistory = history;
    updatedHistory.location.pathname = `/minime/comunicados/mnt/${selectedItem.type}/${selectedItem.id}`;
    return updatedHistory;
  };

  const getNote = () => selectedAppointmentItems[0].note;

  const getStudentName = () => selectedAppointmentItems[0].studentName;

  const renderFormByCommunicationType = () => {
    switch (selectedItem.type) {
      case COMMUNICATION_TYPE.ANALYSIS:
        return (
          <PeriodicAnalysisFormHandler
            match={getMatch()}
            history={getHistory()}
            foreignToggleClose={onCloseEditCommunication}
          />
        );
      case COMMUNICATION_TYPE.ANTHROPOMETRIC:
        return (
          <MeasuresFormHandler
            match={getAnthropometricMatch()}
            foreignToggleClose={onCloseEditCommunication}
          />
        );
      case COMMUNICATION_TYPE.CALENDAR:
        return (
          <EventFormHandler
            match={getMatch()}
            foreignToggleClose={onCloseEditCommunication}
          />
        );

      default:
        return (
          <CommunicationFormHandler
            match={getMatch()}
            history={getHistory()}
            foreignToggleClose={onCloseEditCommunication}
          />
        );
    }
  };

  useImperativeHandle(ref, () => ({
    toggleEditFullScreenForm,
  }));

  return (
    <>
      <FullScreenDialog
        toggleDialog={toggleEditFullScreenForm}
        title={editFullScreenForm.title}
        isOpen={editFullScreenForm.isOpen}
      >
        <>
          {editFullScreenForm.isOpen && (
            <>
              {type === PENDENCY_DETAILS_TYPE.APPOINTMENT && (
                <EditAppointmentModal
                  dto={getFormattedAppointmentObject()}
                  currentValue={getNote()}
                  student={getStudentName()}
                  updateItem={updateAppointmentItem}
                />
              )}
              {type === PENDENCY_DETAILS_TYPE.COMMUNICATION && (
                <>{renderFormByCommunicationType()}</>
              )}
            </>
          )}
        </>
      </FullScreenDialog>
      <div className="pendency-detail">
        <div className="pendency-detail-content">{renderDetailContent()}</div>
        <ButtonToolbar className="justify-content-center">
          <SmartButton
            color="danger"
            outline
            label={t('general.disapprove')}
            onClick={onDisapprove}
          />
          {showEditButton() && (
            <SmartButton
              color="success"
              outline
              label={t('general.edit')}
              onClick={toggleEditFullScreenForm}
            />
          )}
          <SmartButton
            color="success"
            label={t('general.approve')}
            onClick={onApprove}
          />
        </ButtonToolbar>
      </div>
    </>
  );
});

PendencyDetail.propTypes = {
  t: PropTypes.func.isRequired,
  onAppointmentRelations: PropTypes.func.isRequired,
  onCloseEditCommunication: PropTypes.func.isRequired,
  onCommunicationRelations: PropTypes.func.isRequired,
  updateAppointmentItem: PropTypes.func.isRequired,
  type: PropTypes.oneOf([
    PENDENCY_DETAILS_TYPE.APPOINTMENT,
    PENDENCY_DETAILS_TYPE.COMMUNICATION,
  ]).isRequired,
  selectedAppointmentItems: PropTypes.arrayOf(
    PropTypes.shape({
      class: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
      dateNote: PropTypes.string,
      firstLevelItemCode: PropTypes.number,
      hourNote: PropTypes.string,
      id: PropTypes.number,
      idItemNote: PropTypes.number,
      idResponsible: PropTypes.number,
      idStudent: PropTypes.number,
      noteComplement: PropTypes.string,
      typeOfMeal: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      studentName: PropTypes.string,
      note: PropTypes.string,
      valueNote: PropTypes.number,
      type: PropTypes.string,
    })
  ),
  selectedCommunicationItems: PropTypes.arrayOf(
    PropTypes.shape({
      creatorId: PropTypes.number,
      files: PropTypes.arrayOf(
        PropTypes.shape({
          fileName: PropTypes.string,
          fileType: PropTypes.string,
          preview: PropTypes.string,
          status: PropTypes.string,
          type: PropTypes.string,
        })
      ),
      eventHeader: {
        allDay: PropTypes.bool,
        initialDate: PropTypes.number,
        finalDate: PropTypes.number,
        initialHour: PropTypes.string,
        finalHour: PropTypes.string,
        id: PropTypes.number,
        local: PropTypes.string,
      },
      htmlContent: PropTypes.string,
      id: PropTypes.number,
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          description: PropTypes.string,
        })
      ),
      anthropometricMeasurements: anthropometricMeasurementsPropTypes,
      title: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  onDisapprove: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
};

PendencyDetail.defaultProps = {
  selectedCommunicationItems: [],
  selectedAppointmentItems: [],
};

export default PendencyDetail;
